<template>
  <div class="login">
    <vl-region mod-stacked-large>
      <vl-layout>
        <vl-grid mod-stacked>
          <vl-column width="7" width-m="9" width-s="12">
            <vl-title tag-name="h1">{{ $t('login.title') }}</vl-title>
          </vl-column>
          <vl-column>
            <vl-grid>
              <vl-column width="8">
                <vl-grid>
                  <vl-column v-vl-spacer:bottom.large>
                    <vl-introduction>
                      {{ $t('login.introduction') }}
                    </vl-introduction>
                  </vl-column>
                  <vl-column>
                    <vl-grid class="statistics-grid">
                      <template v-if="isFeatureEnabled('publications')">
                        <vl-column>
                          <vl-divider />
                        </vl-column>
                        <vl-column v-if="statisticsLoading">
                          <vl-region>
                            <div v-vl-align:center>
                              <vl-loader :message="$t('login.statistics.loading')" />
                            </div>
                          </vl-region>
                        </vl-column>
                        <vl-column
                          v-if="statistics && !statisticsLoading"
                          v-vl-spacer:bottom.large
                          v-vl-spacer:top.large
                        >
                          <vl-grid>
                            <vl-column
                              v-vl-spacer:bottom.medium
                              width="3"
                              width-s="12"
                              width-xs="12"
                              class="vl-u-flex vl-u-flex-align-center"
                            >
                              <PbsCounterWidget
                                :title="$t('login.statistics.organisations')"
                                :animate="animate"
                                :animationDelay="0"
                                :count="statistics.organisationsCount"
                              />
                            </vl-column>
                            <vl-column
                              v-vl-spacer:bottom.medium
                              width="3"
                              width-s="12"
                              width-xs="12"
                              class="vl-u-flex vl-u-flex-align-center"
                            >
                              <PbsCounterWidget
                                :title="$t('login.statistics.datasets')"
                                :animate="animate"
                                :animationDelay="50"
                                :count="statistics.continuousDatasetsCount + statistics.discreteDatasetsCount"
                              />
                            </vl-column>
                            <vl-column
                              v-vl-spacer:bottom.medium
                              width="3"
                              width-s="12"
                              width-xs="12"
                              class="vl-u-flex vl-u-flex-align-center"
                            >
                              <PbsCounterWidget
                                :title="$t('login.statistics.publications')"
                                :animate="animate"
                                :animationDelay="100"
                                :count="statistics.continuousPublicationsCount + statistics.discretePublicationsCount"
                              />
                            </vl-column>
                            <vl-column
                              v-vl-spacer:bottom.medium
                              width="3"
                              width-s="12"
                              width-xs="12"
                              class="vl-u-flex vl-u-flex-align-center"
                            >
                              <PbsCounterWidget
                                :title="$t('login.statistics.updates')"
                                :animate="animate"
                                :animationDelay="150"
                                :count="statistics.updatesCount"
                              />
                            </vl-column>
                          </vl-grid>
                        </vl-column>
                        <vl-column>
                          <vl-divider />
                        </vl-column>
                      </template>
                    </vl-grid>
                  </vl-column>
                  <vl-column v-vl-spacer:top.large>
                    <vl-grid>
                      <vl-column width="6" width-s="12">
                        <vl-spotlight
                          :title="$t('login.spotlight.about')"
                          :to="`https://www.vlaanderen.be/digitaal-vlaanderen/onze-diensten-en-platformen/datapublicatie`"
                          target="_blank"
                        />
                      </vl-column>
                      <vl-column width="6" width-s="12">
                        <vl-spotlight
                          :title="$t('login.spotlight.faq')"
                          to="https://www.vlaanderen.be/digitaal-vlaanderen/onze-diensten-en-platformen/datapublicatie/datapublicatie-veelgestelde-vragen"
                          target="_blank"
                        />
                      </vl-column>
                    </vl-grid>
                  </vl-column>
                  <vl-column v-vl-spacer:top.large>
                    <vl-title tag-name="h2">{{ $t('login.contact.title') }}</vl-title>
                    <!-- eslint-disable vue/no-v-html -->
                    <p v-html="$t('login.contact.description', { mail: 'digitaal.vlaanderen@vlaanderen.be' })"></p>
                    <!-- eslint-enable vue/no-v-html -->
                  </vl-column>
                </vl-grid>
              </vl-column>
              <vl-column width="4">
                <vl-grid>
                  <vl-column v-vl-spacer:bottom.medium class="login-container">
                    <vl-button mod-large class="login" @click="login">{{ $t('login.login.label') }}</vl-button>
                  </vl-column>
                  <vl-column>
                    <strong>{{ $t('login.needAccess.label') }}</strong>
                  </vl-column>
                  <vl-column>
                    <p>{{ $t('login.needAccess.contact') }}</p>
                  </vl-column>
                </vl-grid>
              </vl-column>
            </vl-grid>
          </vl-column>
          <vl-column> </vl-column>
        </vl-grid>
      </vl-layout>
    </vl-region>
  </div>
</template>
<script lang="ts" setup>
import { isFeatureEnabled } from '@/common/config/config';
import { computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PbsCounterWidget from '@/components/PbsCounterWidget.vue';
import { usePublicationStatisticsStore } from '@/modules/publications/store/publication-statistics.store';

const route = useRoute();
const router = useRouter();
const statisticsStore = usePublicationStatisticsStore();

defineProps({
  animate: {
    type: Boolean,
    required: false,
    default: true
  }
});

function login() {
  router.push({ name: 'login', query: { redirectUri: route.query.redirectUri } });
}

const statistics = computed(() => statisticsStore.statistics);
const statisticsLoading = computed(() => statisticsStore.loading);

onMounted(() => {
  if (route.query.autoRedirect?.toString() === 'true') {
    login();
    return;
  }

  if (isFeatureEnabled('publications')) {
    statisticsStore.getPublicationStatistics();
  }
});

onUnmounted(() => {
  statisticsStore.clear();
});
</script>

<style lang="scss" scoped>
.login-container {
  display: flex;
}
.login {
  flex: 1;
  justify-content: center;
}
.statistics-grid {
  min-height: 250px;
}
:deep(.vl-infotext__text) {
  white-space: nowrap;
}
:deep(.vl-infotext-wrapper) {
  min-width: 250px;
}
</style>
